import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LiveDataFeed = ({ devices, liveData, syncStatus, onForceSync }) => {
  const getDeviceIcon = (deviceType) => {
    const icons = {
      garmin: 'Watch',
      apple_watch: 'Watch',
      whoop: 'Activity',
      hume: 'Watch',
      polar: 'Heart'
    };
    return icons?.[deviceType] || 'Watch';
  };

  const getMetricIcon = (metric) => {
    const icons = {
      heartRate: 'Heart',
      steps: 'Footprints',
      calories: 'Flame',
      sleepHours: 'Moon',
      activeMinutes: 'Clock',
      distance: 'MapPin'
    };
    return icons?.[metric] || 'Activity';
  };

  const getMetricUnit = (metric) => {
    const units = {
      heartRate: 'bpm',
      steps: 'steps',
      calories: 'kcal',
      sleepHours: 'hrs',
      activeMinutes: 'min',
      distance: 'km'
    };
    return units?.[metric] || '';
  };

  const getSyncStatusColor = (status) => {
    if (status === 'syncing') return 'text-warning';
    if (status === 'success') return 'text-success';
    if (status === 'error') return 'text-error';
    return 'text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-xl p-6 elevation-2 h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Activity" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-foreground">Live Data Feed</h2>
            <p className="text-sm text-muted-foreground">Real-time metrics from connected devices</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
          <span className="text-xs font-medium text-success">Live</span>
        </div>
      </div>
      <div className="space-y-4 max-h-[600px] overflow-y-auto">
        {devices?.map((device) => {
          const deviceData = liveData?.[device?.id];
          const status = syncStatus?.[device?.id];

          return (
            <motion.div
              key={device?.id}
              className="bg-muted/30 rounded-xl p-4 border border-wellness-light"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Device Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <Icon name={getDeviceIcon(device?.deviceType)} size={20} className="text-primary" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{device?.deviceName}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {device?.deviceType?.replace('_', ' ')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {status && (
                    <span className={`text-xs font-medium ${getSyncStatusColor(status)}`}>
                      {status === 'syncing' ? 'Syncing...' : status === 'success' ? 'Synced' : 'Error'}
                    </span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onForceSync(device?.id)}
                    disabled={status === 'syncing'}
                  >
                    <Icon name="RefreshCw" size={14} />
                  </Button>
                </div>
              </div>
              {/* Live Metrics */}
              {deviceData ? (
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(deviceData)?.map(([metric, value]) => (
                    <div key={metric} className="bg-card rounded-lg p-3">
                      <div className="flex items-center space-x-2 mb-1">
                        <Icon name={getMetricIcon(metric)} size={14} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground capitalize">
                          {metric?.replace(/([A-Z])/g, ' $1')?.trim()}
                        </span>
                      </div>
                      <div className="flex items-baseline space-x-1">
                        <span className="text-lg font-bold text-foreground">{value}</span>
                        <span className="text-xs text-muted-foreground">{getMetricUnit(metric)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Icon name="Clock" size={24} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">Waiting for data...</p>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default LiveDataFeed;
